import { type ColumnDef } from '@tanstack/react-table';
import type {
  AnalyticalRow,
  ConcentrationUnit,
  ExceedanceDecision,
  ExceedanceLimitDetail,
  NdHandling,
} from '@/api/dataVault';
import { GlowBadge } from '../components/ui/GlowBadge';

// ── Helpers ──────────────────────────────────────────────────────────────

function displayConc(
  val: number | null,
  _unit: ConcentrationUnit,
  detectionLimit?: number | null,
): string {
  if (val === null) return '--';
  // ARGOS policy: backend emits mg/L. No conversion.
  if (detectionLimit != null && detectionLimit > 0) {
    const s = detectionLimit.toFixed(10).replace(/0+$/, '');
    const dotIdx = s.indexOf('.');
    const decimals = dotIdx === -1 ? 0 : s.length - dotIdx - 1;
    return val.toFixed(decimals);
  }
  if (val < 0.001) return val.toExponential(2);
  return val.toFixed(4);
}

/** Drop trailing zeros from a fixed-point string ("0.001000" -> "0.001"). */
function trimZeros(numStr: string): string {
  if (!numStr.includes('.')) return numStr;
  return numStr.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** Whether a row is "exceeding" by the new structured decision, with a
 *  fallback to the legacy boolean for transitional payloads. */
function isExceeding(row: AnalyticalRow): boolean | null {
  if (row.exceedance) return row.exceedance.triggered;
  if (row.exceeds_mcl != null) return row.exceeds_mcl;
  return null;
}

/** Hover-popover that lists every applicable regulatory limit with the
 *  driving one called out with a star. Pure CSS hover (no portal) so it
 *  doesn't fight the table's overflow handling. */
function DrivingLimitTooltip({
  decision,
  children,
}: {
  decision: ExceedanceDecision | null;
  children: React.ReactNode;
}) {
  if (!decision || decision.all_limits.length === 0) {
    return <>{children}</>;
  }
  const limits = [...decision.all_limits].sort((a, b) => a.value - b.value);
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden min-w-[260px]
                   rounded border border-mono-700 bg-mono-950/95 p-2.5 shadow-xl
                   group-hover:block"
      >
        <span className="mb-1.5 block text-[10px] tracking-widest text-mono-500">
          REGULATORY LIMITS · rule: <span className="text-mono-300">{decision.rule}</span>
        </span>
        <span className="block divide-y divide-mono-800/60 text-xs">
          {limits.map((l: ExceedanceLimitDetail) => {
            const isDriving =
              decision.driving_limit?.library === l.library &&
              decision.driving_limit?.label === l.label;
            return (
              <span key={`${l.library}-${l.label}`} className="flex items-center justify-between gap-3 py-1">
                <span className="flex items-center gap-1.5 truncate">
                  {isDriving ? (
                    <span aria-label="driving limit" className="text-amber-300">★</span>
                  ) : (
                    <span className="w-2.5" />
                  )}
                  <span className={l.exceeded ? 'text-red-300' : 'text-mono-300'}>{l.label}</span>
                </span>
                <span className="font-mono text-mono-200 tabular-nums">
                  {trimZeros(l.value.toFixed(10))} {l.units}
                </span>
              </span>
            );
          })}
        </span>
        <span className="mt-2 block text-[10px] leading-snug text-mono-500">
          {decision.reason}
        </span>
      </span>
    </span>
  );
}

// ── Column Factory ───────────────────────────────────────────────────────

export interface ColumnFactoryOptions {
  unit: ConcentrationUnit;
  ndHandling: NdHandling;
  anomalyMap: Map<string, { z_score: number; direction: string }>;
  showAnomalies: boolean;
  onSelectWell?: (wellId: string) => void;
}

export function createAnalyticalColumns(
  opts: ColumnFactoryOptions,
): ColumnDef<AnalyticalRow, any>[] {
  const { unit, ndHandling, anomalyMap, showAnomalies, onSelectWell } = opts;

  const columns: ColumnDef<AnalyticalRow, any>[] = [
    // 0 ── QA — unit-anomaly warning. Renders a ⚠ icon on rows the
    // intake validator flagged (and an operator hasn't yet reviewed).
    // Hovering shows the validator's reason + suggested median.
    {
      id: 'qa',
      header: '',
      enableSorting: false,
      size: 24,
      cell: ({ row }) => {
        const r = row.original;
        if (!r.qa_flag || r.qa_review_action) return null;
        const detail = r.qa_flag_detail;
        const tooltip =
          detail?.reason ||
          `Unit anomaly flagged: ${r.qa_flag}.`;
        return (
          <span
            role="img"
            aria-label={tooltip}
            title={tooltip}
            className="inline-flex h-4 w-4 items-center justify-center rounded-sm
                       border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px]"
          >
            ⚠
          </span>
        );
      },
    },

    // 1 ── SITE
    {
      id: 'site_name',
      accessorKey: 'site_name',
      header: 'SITE',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-mono-100 max-w-[200px] truncate block">
          {getValue<string>()}
        </span>
      ),
    },

    // 2 ── WELL
    {
      id: 'well_name',
      accessorKey: 'well_name',
      header: 'WELL',
      enableSorting: true,
      cell: ({ row }) => {
        const name = row.original.well_name;
        const wellId = row.original.well_id;
        if (!name) return <span className="text-mono-400">--</span>;
        if (onSelectWell && wellId) {
          return (
            <button
              className="text-cyan-400 cursor-pointer hover:underline"
              onClick={() => onSelectWell(wellId)}
            >
              {name}
            </button>
          );
        }
        return <span>{name}</span>;
      },
    },

    // 3 ── DATE
    {
      id: 'sample_date',
      accessorKey: 'sample_date',
      header: 'DATE',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-mono-200">{getValue<string>()}</span>
      ),
    },

    // 4 ── COMPOUND
    {
      id: 'compound',
      accessorKey: 'compound',
      header: 'COMPOUND',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-mono-100">{getValue<string>()}</span>
      ),
    },

    // 5 ── CONC
    {
      id: 'concentration',
      accessorKey: 'concentration',
      header: `CONC (${unit})`,
      enableSorting: true,
      cell: ({ row }) => {
        const r = row.original;
        const { concentration, detection_limit, is_non_detect, mcl, exceedance } = r;
        const exceeds = isExceeding(r);

        if (concentration === null) {
          return <span className="text-mono-400">--</span>;
        }

        // ND mode "less_than": show "<{detection_limit}" — the form labs use
        // on regulatory reports. Highlight in red if the detection limit
        // itself exceeds any applicable regulatory limit (a diluted-out ND
        // that still can't prove compliance).
        if (is_non_detect && ndHandling === 'less_than') {
          if (detection_limit == null) {
            return <span className="italic text-mono-300">ND</span>;
          }
          const dlStr = trimZeros(detection_limit.toFixed(10));
          // Compare DL against the strictest applicable limit, not just MCL.
          const strictest = exceedance?.driving_limit?.value ?? mcl;
          const dlExceeds = strictest != null && strictest > 0 && detection_limit > strictest;
          return (
            <DrivingLimitTooltip decision={exceedance}>
              <span className={dlExceeds ? 'text-red-400 font-medium' : 'italic text-mono-300'}>
                &lt;{dlStr}
              </span>
            </DrivingLimitTooltip>
          );
        }

        const formatted = displayConc(concentration, unit, detection_limit);

        if (is_non_detect) {
          return (
            <DrivingLimitTooltip decision={exceedance}>
              <span className="italic text-mono-300">{formatted} ND</span>
            </DrivingLimitTooltip>
          );
        }
        if (exceeds === true) {
          return (
            <DrivingLimitTooltip decision={exceedance}>
              <span className="text-red-400 font-medium">{formatted}</span>
            </DrivingLimitTooltip>
          );
        }
        if (exceeds === false && (exceedance?.driving_limit || mcl !== null)) {
          return (
            <DrivingLimitTooltip decision={exceedance}>
              <span className="text-emerald-400">{formatted}</span>
            </DrivingLimitTooltip>
          );
        }
        return <span className="text-mono-100">{formatted}</span>;
      },
    },

    // 6 ── Regulatory Limit (driving limit when set, else legacy MCL)
    {
      id: 'mcl',
      accessorKey: 'mcl',
      header: 'REG LIMIT',
      enableSorting: true,
      cell: ({ row }) => {
        const { mcl, exceedance } = row.original;
        const driving = exceedance?.driving_limit ?? null;
        const value = driving?.value ?? mcl;
        if (value == null) return <span className="text-mono-400">--</span>;
        return (
          <span className="flex flex-col leading-tight">
            <span className="text-mono-200">{displayConc(value, unit)}</span>
            {driving && driving.label !== 'EPA MCL' && (
              <span className="text-[10px] tracking-wider text-mono-500">{driving.label}</span>
            )}
          </span>
        );
      },
    },

    // 7 ── STATUS
    {
      id: 'status',
      header: 'STATUS',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        const exceeds = isExceeding(r);
        const hasLimit = r.exceedance?.driving_limit != null || r.mcl != null;
        return (
          <span className="flex items-center gap-1">
            {exceeds === true && (
              <GlowBadge variant="danger">EXCEEDS</GlowBadge>
            )}
            {exceeds === false && hasLimit && (
              <GlowBadge variant="success">BELOW</GlowBadge>
            )}
            {r.is_non_detect && (
              <GlowBadge variant="neutral" className="ml-1">ND</GlowBadge>
            )}
          </span>
        );
      },
    },

    // 8 ── FLAG — analytical lab qualifier (U, J, BRL, D, etc.)
    // Source: Talon LPE Flag Codes.xlsx (analytical file).
    {
      id: 'qualifier',
      accessorKey: 'qualifier',
      header: 'FLAG',
      enableSorting: true,
      cell: ({ getValue }) => {
        const code = getValue<string | null>();
        if (!code) return <span className="text-mono-500">--</span>;
        return <span className="text-amber-300 font-mono text-xs">{code}</span>;
      },
    },

    // 9 ── METHOD (always last; anomaly inserted before if needed)
    {
      id: 'lab_method',
      accessorKey: 'lab_method',
      header: 'METHOD',
      enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue<string | null>();
        if (!v) return <span className="text-mono-400">--</span>;
        return <span className="text-mono-200">{v}</span>;
      },
    },
  ];

  // 8 ── ANOMALY (conditionally inserted before METHOD)
  if (showAnomalies) {
    const methodIdx = columns.findIndex((c) => c.id === 'lab_method');
    columns.splice(methodIdx, 0, {
      id: 'anomaly',
      header: 'ANOMALY',
      enableSorting: false,
      cell: ({ row }) => {
        const anomaly = anomalyMap.get(row.original.id);
        if (!anomaly) return null;
        const arrow = anomaly.direction === 'high' ? '\u2191' : '\u2193';
        const variant = anomaly.direction === 'high' ? 'danger' : 'info';
        return (
          <GlowBadge variant={variant}>
            {arrow} {anomaly.z_score.toFixed(1)}\u03c3
          </GlowBadge>
        );
      },
    } as ColumnDef<AnalyticalRow, any>);
  }

  return columns;
}
